// Per-dialect synthesizer for table-lifecycle ops — rename-table +
// move-table-schema (v2 Slice D1).
//
// PRD: Ralph/DdlStructuralEdit2/PRD.md § Slice D1.
//
// Per-engine surface:
//   - mssql:  rename → EXEC sp_rename '<schema.tbl>', '<new>'
//             move   → ALTER SCHEMA <newSchema> TRANSFER <fromTable>
//   - pgsql:  rename → ALTER TABLE <fromTable> RENAME TO <new>
//             move   → ALTER TABLE <fromTable> SET SCHEMA <newSchema>
//   - mysql:  rename → RENAME TABLE <fromTable> TO [<newSchema>.]<new>
//             move   → unsupported (use rename with mysqlToSchema instead)
//   - oracle: rename → RENAME <fromTable.bare> TO <new> (bare-name target)
//             move   → unsupported (oracle schema == user; consumer migrates
//                      via CREATE TABLE … AS SELECT + DROP)
//   - sqlite: rename → ALTER TABLE <fromTable> RENAME TO <new>
//             move   → unsupported (no DDL schemas)
//
// Identifier handling reuses `maybeQuoteIdent` from `dialect.ts`. The source
// table's `raw` text carries through where it preserves source-quoting; the
// target identifiers (`toName`, `toSchema`) are bare callers' input — the
// synthesizer does not requote them.

import type { QualifiedName, SqlEngine } from '../../../sql-grammar/model';
import { DdlEditError } from '../../types';

import { maybeQuoteIdent } from './dialect';
import { needsQuoting } from './dialect.quoting';

function tableText(engine: SqlEngine, table: QualifiedName): string {
  if (table.raw && table.raw.length > 0) return table.raw;
  const name = maybeQuoteIdent(engine, table.name, table.quoted);
  if (table.schema) {
    const schema = maybeQuoteIdent(engine, table.schema, table.quoted);
    return `${schema}.${name}`;
  }
  return name;
}

function unsupported(engine: SqlEngine, op: string, hint: string): never {
  throw new DdlEditError(
    'engine-does-not-support-op',
    `applyDdlEdit: ${engine} does not support ${op} — ${hint}`,
  );
}

export function synthesizeRenameTable(
  engine: SqlEngine,
  fromTable: QualifiedName,
  toName: string,
  mysqlToSchema?: string,
): string {
  if (engine !== 'mysql' && mysqlToSchema !== undefined) {
    throw new Error(
      `applyDdlEdit: ${engine} rename-table does not accept 'mysqlToSchema' — `
        + `cross-database rename is mysql-specific. Use 'move-table-schema' on `
        + `mssql / pgsql, or a CREATE-AS-SELECT migration on oracle / sqlite.`,
    );
  }
  if (engine === 'mssql') {
    const tbl = tableText(engine, fromTable);
    return `EXEC sp_rename '${tbl}', '${toName}'`;
  }
  if (engine === 'mysql') {
    const target = mysqlToSchema !== undefined
      ? `${maybeQuoteIdent(engine, mysqlToSchema, fromTable.quoted || needsQuoting(mysqlToSchema))}.`
        + `${maybeQuoteIdent(engine, toName, fromTable.quoted || needsQuoting(toName))}`
      : maybeQuoteIdent(engine, toName, fromTable.quoted || needsQuoting(toName));
    return `RENAME TABLE ${tableText(engine, fromTable)} TO ${target}`;
  }
  if (engine === 'oracle') {
    // Oracle's RENAME does not accept a schema qualifier on either side —
    // the table being renamed must live in the current user's schema, and
    // the new name is bare. Surface the constraint upfront for callers.
    if (fromTable.schema && fromTable.schema.length > 0) {
      throw new Error(
        `applyDdlEdit: oracle rename-table requires the source table to live `
          + `in the current schema (oracle 'RENAME old TO new' has no schema `
          + `qualifier). The provided 'fromTable' carries schema `
          + `'${fromTable.schema}'; switch sessions to that schema or use a `
          + `CREATE-AS-SELECT migration.`,
      );
    }
    const oldName = maybeQuoteIdent(engine, fromTable.name, fromTable.quoted);
    const newName = maybeQuoteIdent(engine, toName, fromTable.quoted || needsQuoting(toName));
    return `RENAME ${oldName} TO ${newName}`;
  }
  // pgsql + sqlite share the ALTER TABLE … RENAME TO syntax.
  const newName = maybeQuoteIdent(engine, toName, fromTable.quoted || needsQuoting(toName));
  return `ALTER TABLE ${tableText(engine, fromTable)} RENAME TO ${newName}`;
}

export function synthesizeMoveTableSchema(
  engine: SqlEngine,
  fromTable: QualifiedName,
  toSchema: string,
): string {
  if (engine === 'mssql') {
    const newSchema = maybeQuoteIdent(engine, toSchema, fromTable.quoted || needsQuoting(toSchema));
    return `ALTER SCHEMA ${newSchema} TRANSFER ${tableText(engine, fromTable)}`;
  }
  if (engine === 'postgres') {
    const newSchema = maybeQuoteIdent(engine, toSchema, fromTable.quoted || needsQuoting(toSchema));
    return `ALTER TABLE ${tableText(engine, fromTable)} SET SCHEMA ${newSchema}`;
  }
  if (engine === 'mysql') {
    unsupported(engine, 'move-table-schema',
      `mysql conflates schemas with databases. Use op:'rename-table' with `
        + `'mysqlToSchema' set to the target database for the cross-database-`
        + `rename equivalent (e.g. RENAME TABLE old_db.tbl TO new_db.tbl).`);
  }
  if (engine === 'oracle') {
    unsupported(engine, 'move-table-schema',
      `oracle conflates schemas with users. Moving a table between schemas `
        + `requires a CREATE TABLE … AS SELECT + DROP TABLE migration that `
        + `v2 leaves to the consumer.`);
  }
  // sqlite
  unsupported(engine, 'move-table-schema',
    `sqlite has no DDL schemas. Attach an alternate database file via `
      + `'ATTACH DATABASE' if a logical separation is needed.`);
}
